import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Award,
    Boxes,
    Sparkles,
    Waves,
    ChevronDown,
    ExternalLink,
    MapPin,
    Phone,
    Clock,
    ArrowRight,
    Star,
    HelpCircle,
    Mail,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { BIZ, IMG, CATEGORIES, SOCIAL_LINKS, reserveBottleMailto } from "../lib/constants";
import { api } from "../lib/api";
import { StorePhoto, storePhotoUrl, STORE_PHOTOS } from "../components/StorePhoto";

// Category → representative image map (used when live data arrives)
const CAT_IMG = {
    bourbon: IMG.bourbonShelf,
    whiskey: IMG.whiskeyPourAmber,
    scotch: IMG.whiskeyPourAmber,
    tequila: IMG.cocktailOldFashioned,
    mezcal: IMG.cocktailOldFashioned,
    vodka: IMG.heroBottlesDark,
    gin: IMG.heroBottlesDark,
    rum: IMG.heroBottlesDark,
    wine: IMG.wineElegant,
    beer: IMG.craftBeer,
    seltzer: IMG.craftBeer,
    liqueur: IMG.heroBottlesDark,
    cigars: IMG.bourbonShelf,
};
const CAT_LABEL = {
    bourbon: "Bourbon", whiskey: "Whiskey", scotch: "Scotch",
    tequila: "Tequila", mezcal: "Mezcal", vodka: "Vodka", gin: "Gin",
    rum: "Rum", wine: "Wine", beer: "Craft Beer", seltzer: "Hard Seltzer",
    liqueur: "Liqueur", cigars: "Cigars",
};
const CAT_BLURB = {
    bourbon: "Single barrels, allocated picks, Kentucky royalty.",
    whiskey: "American, Tennessee, Canadian — the long pour.",
    scotch: "Speyside benchmarks to peated Islay.",
    tequila: "Blanco to extra añejo. 100% agave.",
    mezcal: "Underground smoke. Espadín & beyond.",
    vodka: "Clean classics & craft small-batch.",
    gin: "London Dry to modern botanical.",
    rum: "White, dark, spiced — Caribbean to Cuban.",
    wine: "Old world, new world, natural & sparkling.",
    beer: "Missouri local, IPAs, stouts, seasonals.",
    seltzer: "Real-vodka cans, hard seltzers, RTDs.",
    liqueur: "Aperitivo, after-dinner, classics.",
    cigars: "Honduran, Dominican, Nicaraguan blends.",
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
};

const TRUST = [
    { icon: Boxes, title: "Locally Owned & Operated", body: "A Henry County independent." },
    { icon: Award, title: "Largest Selection in Henry County", body: "Over 3,000 SKUs and growing." },
    { icon: Sparkles, title: "Expert Staff", body: "Real recommendations, never algorithms." },
    { icon: Waves, title: "Curated for Truman Lake", body: "Stocked for your weekend, not a warehouse." },
];

const FALLBACK_REVIEWS = [
    { author_name: "Jessica M.", author_location: "Clinton, MO", body: "Best selection of bourbon I've found anywhere in Henry County. The staff actually knows what they're talking about.", rating: 5 },
    { author_name: "Robert K.", author_location: "Warsaw, MO", body: "We stop here every Friday on our way to Truman Lake. They know what we want before we walk in.", rating: 5 },
    { author_name: "Sarah T.", author_location: "Sedalia, MO", body: "It's not just a liquor store — it feels curated. Wine recommendations are spot on, every time.", rating: 5 },
];

const baseJsonLd = {
    "@context": "https://schema.org",
    "@type": ["LiquorStore", "LocalBusiness"],
    "@id": `${BIZ.siteUrl}/#business`,
    name: BIZ.name,
    image: `${BIZ.siteUrl}/og-image.png`,
    url: BIZ.siteUrl,
    telephone: BIZ.phone,
    priceRange: "$$",
    paymentAccepted: ["Cash", "Credit Card", "Debit Card", "Apple Pay", "Google Pay"],
    currenciesAccepted: "USD",
    slogan: BIZ.tagline,
    foundingDate: "2010",
    hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BIZ.address.full)}`,
    geo: { "@type": "GeoCoordinates", latitude: 38.369, longitude: -93.782 },
    openingHoursSpecification: [
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"], opens: "10:00", closes: "22:00" },
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Friday", "Saturday"], opens: "10:00", closes: "23:00" },
        { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "10:00", closes: "22:00" },
    ],
    address: {
        "@type": "PostalAddress",
        streetAddress: BIZ.address.line1,
        addressLocality: BIZ.address.city,
        addressRegion: BIZ.address.state,
        postalCode: BIZ.address.zip,
        addressCountry: BIZ.address.country,
    },
    areaServed: BIZ.areasServed.map((a) => ({ "@type": "Place", name: a })),
    sameAs: SOCIAL_LINKS.map((s) => s.url),
};

const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BIZ.siteUrl}/#website`,
    url: BIZ.siteUrl,
    name: BIZ.name,
    publisher: { "@id": `${BIZ.siteUrl}/#business` },
    potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${BIZ.siteUrl}/spirits?q={search_term_string}` },
        "query-input": "required name=search_term_string",
    },
};

const organizationLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${BIZ.siteUrl}/#org`,
    name: BIZ.name,
    url: BIZ.siteUrl,
    logo: `${BIZ.siteUrl}/og-image.png`,
    sameAs: SOCIAL_LINKS.map((s) => s.url),
    contactPoint: [{
        "@type": "ContactPoint",
        telephone: BIZ.phone,
        email: BIZ.email,
        contactType: "customer service",
        areaServed: "US-MO",
        availableLanguage: ["English"],
    }],
};

export default function Home() {
    const [liveCats, setLiveCats] = useState(null);
    const [latestPosts, setLatestPosts] = useState(null);
    const [recipes, setRecipes] = useState(null);
    const [attractions, setAttractions] = useState(null);
    const [reviewSummary, setReviewSummary] = useState(null);
    const [liveReviews, setLiveReviews] = useState(null);
    const [trendingFaqs, setTrendingFaqs] = useState(null);

    useEffect(() => {
        api.productCategories()
            .then((d) => setLiveCats((d.categories || []).slice(0, 6)))
            .catch(() => setLiveCats([]));
        api.blog({ limit: 3 })
            .then((d) => setLatestPosts(d.items || []))
            .catch(() => setLatestPosts([]));
        api.recipes({ limit: 3 })
            .then((d) => setRecipes(d.items || []))
            .catch(() => setRecipes([]));
        api.attractions()
            .then((d) => setAttractions((d.items || []).slice(0, 3)))
            .catch(() => setAttractions([]));
        api.reviewsSummary()
            .then(setReviewSummary)
            .catch(() => setReviewSummary(null));
        api.reviews({ limit: 3, status: "approved" })
            .then((d) => setLiveReviews(d.items || []))
            .catch(() => setLiveReviews(null));
        api.faqs()
            .then((d) => setTrendingFaqs((d.items || []).slice(0, 6)))
            .catch(() => setTrendingFaqs([]));
    }, []);

    const jsonLd = useMemo(() => {
        const out = { ...baseJsonLd };
        if (reviewSummary && reviewSummary.count) {
            out.aggregateRating = {
                "@type": "AggregateRating",
                ratingValue: String(reviewSummary.average),
                reviewCount: reviewSummary.count,
                bestRating: "5",
                worstRating: "1",
            };
        }
        return out;
    }, [reviewSummary]);

    const displayReviews = (liveReviews && liveReviews.length ? liveReviews : FALLBACK_REVIEWS).slice(0, 3);

    return (
        <>
            <SEO
                title="Clinton's Premier Spirits Destination"
                description="Liquor Studio — Clinton, Missouri's curated bourbon, whiskey, wine and craft beer destination. 309 S 2nd St, Clinton, MO. Shop online or visit our store."
                path="/"
                jsonLd={[jsonLd, websiteLd, organizationLd]}
            />

            {/* HERO — REAL STORE entrance photo (replaces AI bottles) */}
            <section
                aria-label="Hero"
                data-testid="hero-section"
                data-onscrim="true"
                className="relative grain"
                style={{ minHeight: "100vh", marginTop: -72, paddingTop: 72, isolation: "isolate" }}
            >
                <img
                    src={storePhotoUrl("entrance-stone-wall", "jpg")}
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    decoding="async"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }}
                />
                <div className="absolute inset-0 hero-overlay" aria-hidden="true" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.75) 60%, rgba(0,0,0,0.88) 100%)", zIndex: 1 }} />

                <div className="relative ls-container" style={{ minHeight: "calc(100vh - 72px)" }}>
                    <div
                        className="flex flex-col justify-center"
                        style={{ minHeight: "calc(100vh - 72px)", paddingTop: 80, paddingBottom: 80 }}
                    >
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            transition={{ duration: 0.7 }}
                            className="max-w-3xl"
                        >
                            <span className="pill" data-testid="hero-pill">
                                <span aria-hidden="true">●</span> 21+ Only · Licensed Missouri Retailer
                            </span>
                        </motion.div>

                        <motion.h1
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            transition={{ duration: 0.8, delay: 0.15 }}
                            className="font-display text-white mt-6"
                            style={{
                                fontSize: "clamp(48px, 8vw, 96px)",
                                lineHeight: 1.02,
                                fontWeight: 500,
                                letterSpacing: "-0.02em",
                                maxWidth: 900,
                            }}
                            data-testid="hero-headline"
                        >
                            Clinton's{" "}
                            <em
                                style={{
                                    fontStyle: "italic",
                                    color: "var(--ls-gold)",
                                    fontWeight: 400,
                                }}
                            >
                                Liquor Studio
                            </em>
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="mt-6"
                            style={{
                                color: "var(--ls-text-secondary)",
                                fontSize: "clamp(16px, 1.5vw, 20px)",
                                maxWidth: 620,
                                lineHeight: 1.6,
                            }}
                            data-testid="hero-subhead"
                        >
                            Premier spirits, wine & craft beer in the heart of Missouri.
                            A curated destination for bourbon lovers, weekend lake-goers
                            and everyone in between.
                        </motion.p>

                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            transition={{ duration: 0.8, delay: 0.45 }}
                            className="mt-10 flex flex-col sm:flex-row gap-4"
                        >
                            <a
                                href={BIZ.shopUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary"
                                data-testid="hero-shop-cta"
                            >
                                Shop Online <ExternalLink size={16} aria-hidden="true" />
                            </a>
                            <Link to="/visit" className="btn-outline-gold" data-testid="hero-visit-cta">
                                Visit Our Store <ArrowRight size={16} aria-hidden="true" />
                            </Link>
                        </motion.div>
                    </div>

                    {/* Scroll indicator */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4, duration: 1, repeat: Infinity, repeatType: "reverse" }}
                        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center"
                        aria-hidden="true"
                        style={{ color: "var(--ls-text-secondary)" }}
                    >
                        <span className="text-xs uppercase tracking-[0.3em] mb-2">Scroll</span>
                        <ChevronDown size={20} />
                    </motion.div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section
                aria-label="What we stand for"
                data-testid="trust-strip"
                style={{
                    borderTop: "1px solid var(--ls-border)",
                    borderBottom: "1px solid var(--ls-border)",
                    background: "var(--ls-bg)",
                }}
            >
                <div className="ls-container py-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                        {TRUST.map((t, i) => (
                            <motion.div
                                key={t.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="flex flex-col"
                            >
                                <t.icon
                                    size={26}
                                    style={{ color: "var(--ls-gold)" }}
                                    aria-hidden="true"
                                />
                                <h3
                                    className="font-display mt-4 text-white"
                                    style={{ fontSize: 20, fontWeight: 500, lineHeight: 1.2 }}
                                >
                                    {t.title}
                                </h3>
                                <p
                                    className="mt-2 text-sm"
                                    style={{ color: "var(--ls-text-secondary)" }}
                                >
                                    {t.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STORY TEASER */}
            <section className="section-y" aria-label="Our Story" data-testid="story-teaser">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -24 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="eyebrow">Our Story</span>
                            <h2
                                className="font-display mt-5 text-white"
                                style={{
                                    fontSize: "clamp(36px, 5vw, 60px)",
                                    fontWeight: 500,
                                    lineHeight: 1.05,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                More than a store.<br />
                                <em
                                    style={{
                                        fontStyle: "italic",
                                        color: "var(--ls-gold)",
                                        fontWeight: 400,
                                    }}
                                >
                                    A studio.
                                </em>
                            </h2>
                            <p
                                className="mt-6"
                                style={{
                                    color: "var(--ls-text-secondary)",
                                    fontSize: 17,
                                    lineHeight: 1.7,
                                }}
                            >
                                We built Liquor Studio because Clinton deserved more than
                                fluorescent lights and dusty shelves. Every bottle is hand-picked.
                                Every recommendation is from someone who's actually tasted it.
                                Whether you're hosting on Truman Lake or pouring a quiet
                                Tuesday old fashioned, we'll have the right one.
                            </p>
                            <Link to="/about" className="btn-outline-gold mt-8" data-testid="story-about-cta">
                                Read Our Story <ArrowRight size={16} />
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 24 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="relative"
                        >
                            <StorePhoto
                                slug="wine-aisle"
                                alt="Liquor Studio Clinton MO wine aisle with hundreds of bottles — a real photo of our store"
                                loading="lazy"
                                style={{
                                    aspectRatio: "4/5",
                                    borderRadius: 6,
                                    border: "1px solid var(--ls-border-strong)",
                                }}
                                testid="story-wine-aisle"
                            />
                            <div
                                className="absolute -bottom-6 -left-6 hidden md:block px-6 py-5"
                                style={{
                                    background: "var(--ls-bg)",
                                    border: "1px solid var(--ls-gold)",
                                    borderRadius: 4,
                                }}
                            >
                                <span className="font-bebas text-white" style={{ fontSize: 36, lineHeight: 1 }}>
                                    3,000+
                                </span>
                                <p className="text-xs mt-1" style={{ color: "var(--ls-text-secondary)" }}>
                                    Curated SKUs
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* SIGNATURE CATEGORIES */}
            <section
                id="categories"
                className="section-y"
                style={{ background: "#070707" }}
                aria-label="Signature Categories"
                data-testid="categories-section"
            >
                <div className="ls-container">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <span className="eyebrow">Signature Categories</span>
                            <h2
                                className="font-display mt-4"
                                style={{
                                    fontSize: "clamp(36px, 5vw, 56px)",
                                    fontWeight: 500,
                                    lineHeight: 1.05,
                                    letterSpacing: "-0.02em",
                                    color: "var(--ls-text)",
                                }}
                            >
                                Curated for{" "}
                                <em className="accent-gold">
                                    every pour
                                </em>
                            </h2>
                        </div>
                        <a
                            href={BIZ.shopUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost"
                            data-testid="categories-shopall-cta"
                        >
                            Shop the full catalog <ExternalLink size={14} />
                        </a>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-categories-grid">
                        {(() => {
                            const display = liveCats === null
                                ? Array.from({ length: 6 }, (_, i) => ({ _skeleton: true, id: `sk-${i}` }))
                                : liveCats.length > 0
                                    ? liveCats.map((c) => ({
                                        id: c.category,
                                        name: CAT_LABEL[c.category] || c.category,
                                        blurb: CAT_BLURB[c.category] || `${c.count} bottle${c.count > 1 ? "s" : ""} in stock.`,
                                        img: CAT_IMG[c.category] || IMG.heroBottlesDark,
                                        count: c.count,
                                        live: true,
                                    }))
                                    : CATEGORIES.map((c) => ({ ...c, img: IMG[c.img] || IMG.heroBottlesDark, live: false }));

                            return display.map((c, i) => {
                                if (c._skeleton) {
                                    return (
                                        <div key={c.id} className="cat-card" aria-hidden="true" data-testid={`home-cat-skel-${i}`}>
                                            <div style={{ aspectRatio: "4/5", background: "linear-gradient(110deg, #161616 30%, #1f1f1f 50%, #161616 70%)", backgroundSize: "200% 100%", animation: "skel 1.4s ease-in-out infinite" }} />
                                        </div>
                                    );
                                }
                                const linkProps = c.live
                                    ? { as: Link, to: `/spirits/category/${c.id}` }
                                    : { as: "a", href: BIZ.shopUrl, target: "_blank", rel: "noopener noreferrer" };
                                const Tag = linkProps.as;
                                const tagProps = { ...linkProps }; delete tagProps.as;
                                return (
                                    <motion.div
                                        key={c.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 0.6, delay: i * 0.06 }}
                                    >
                                        <Tag {...tagProps} className="cat-card group block" data-testid={`category-card-${c.id}`} aria-label={`Browse ${c.name}`}>
                                            <div className="relative overflow-hidden" style={{ aspectRatio: "4/5" }}>
                                                <img src={c.img || c.image_url} alt={`${c.name} selection at Liquor Studio`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                <div className="absolute inset-0 panel-overlay" />
                                                <div className="absolute inset-x-0 bottom-0 p-6">
                                                    {c.count != null && (
                                                        <span className="pill" style={{ fontSize: 10, padding: "3px 10px" }}>{c.count} in stock</span>
                                                    )}
                                                    <h3 className="font-display text-white mt-3" style={{ fontSize: 26, fontWeight: 500, lineHeight: 1.1 }}>{c.name}</h3>
                                                    <p className="mt-2 text-sm" style={{ color: "var(--ls-text-secondary)" }}>{c.blurb}</p>
                                                    <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium" style={{ color: "var(--ls-gold)" }}>
                                                        Explore <ArrowRight size={14} />
                                                    </span>
                                                </div>
                                            </div>
                                        </Tag>
                                    </motion.div>
                                );
                            });
                        })()}
                    </div>
                </div>
            </section>

            {/* TRUMAN LAKE BANNER */}
            <section
                aria-label="Truman Lake Experience"
                data-testid="truman-banner"
                className="relative grain"
                style={{ minHeight: "70vh" }}
            >
                <div
                    className="absolute inset-0"
                    aria-hidden="true"
                    style={{
                        backgroundImage: `url(${IMG.trumanLake})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div
                    className="absolute inset-0"
                    aria-hidden="true"
                    style={{
                        background: "linear-gradient(90deg, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.35) 60%, rgba(10,10,10,0) 100%)",
                    }}
                />
                <div
                    className="relative ls-container flex items-center"
                    style={{ minHeight: "70vh", paddingTop: 96, paddingBottom: 96 }}
                >
                    <div className="max-w-xl">
                        <span className="eyebrow">The Truman Lake Experience</span>
                        <h2
                            className="font-display mt-5 text-white"
                            style={{
                                fontSize: "clamp(36px, 5vw, 60px)",
                                fontWeight: 500,
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Your lakeside weekend starts at{" "}
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                Liquor Studio.
                            </em>
                        </h2>
                        <p
                            className="mt-6"
                            style={{
                                color: "var(--ls-text-secondary)",
                                fontSize: 17,
                                lineHeight: 1.7,
                            }}
                        >
                            We stock the cooler-fillers, the long-pour bourbons and the
                            celebratory bottles every Truman Lake weekend deserves.
                            Just two minutes from downtown Clinton.
                        </p>
                        <Link to="/visit" className="btn-primary mt-8" data-testid="truman-cta">
                            Plan Your Visit <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* FROM OUR STUDIO JOURNAL */}
            <section className="section-y" aria-label="From our Studio Journal" data-testid="home-journal-section">
                <div className="ls-container">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                        <div>
                            <span className="eyebrow">From Our Studio Journal</span>
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                                Read, then <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>pour.</em>
                            </h2>
                        </div>
                        <Link to="/blog" className="btn-ghost" data-testid="home-journal-all">All articles <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-journal-grid">
                        {latestPosts === null
                            ? Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="cat-card" aria-hidden="true" data-testid={`journal-skel-${i}`}>
                                    <div style={{ aspectRatio: "16/10", background: "linear-gradient(110deg, #161616 30%, #1f1f1f 50%, #161616 70%)", backgroundSize: "200% 100%", animation: "skel 1.4s ease-in-out infinite" }} />
                                    <div className="p-5"><div style={{ height: 14, background: "#1a1a1a", borderRadius: 3, width: "80%" }} /><div style={{ height: 14, background: "#1a1a1a", borderRadius: 3, width: "60%", marginTop: 8 }} /></div>
                                </div>
                            ))
                            : latestPosts.length === 0
                                ? <p style={{ color: "var(--ls-text-muted)" }} data-testid="home-journal-empty">New articles coming soon.</p>
                                : latestPosts.map((p, i) => (
                                    <motion.div key={p.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                                        <Link to={`/blog/${p.slug}`} className="cat-card block" data-testid={`home-journal-card-${p.slug}`} aria-label={p.title}>
                                            <div style={{ aspectRatio: "16/10", overflow: "hidden" }}>
                                                <img src={p.cover_image_url} alt={p.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            </div>
                                            <div className="p-6">
                                                <span className="pill" style={{ fontSize: 9, padding: "3px 8px" }}>{p.category}</span>
                                                <h3 className="font-display text-white mt-3" style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.25 }}>{p.title}</h3>
                                                <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                                    <Clock size={12} /> {p.reading_minutes} min read
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))
                        }
                    </div>
                </div>
            </section>

            {/* TONIGHT'S COCKTAIL */}
            <section className="section-y" style={{ background: "#070707", borderTop: "1px solid var(--ls-border)", borderBottom: "1px solid var(--ls-border)" }} aria-label="Tonight's cocktail" data-testid="home-cocktail-section">
                <div className="ls-container">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                        <div>
                            <span className="eyebrow">Tonight's Cocktail</span>
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                                Stir, shake,<br /><em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>then sit down.</em>
                            </h2>
                        </div>
                        <Link to="/recipes" className="btn-ghost" data-testid="home-recipes-all">All recipes <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-cocktail-grid">
                        {recipes === null
                            ? Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="cat-card" aria-hidden="true" data-testid={`cocktail-skel-${i}`}>
                                    <div style={{ aspectRatio: "4/5", background: "linear-gradient(110deg, #161616 30%, #1f1f1f 50%, #161616 70%)", backgroundSize: "200% 100%", animation: "skel 1.4s ease-in-out infinite" }} />
                                </div>
                            ))
                            : recipes.length === 0
                                ? <p style={{ color: "var(--ls-text-muted)" }} data-testid="home-cocktail-empty">Recipes coming soon.</p>
                                : recipes.map((r, i) => (
                                    <motion.div key={r.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                                        <Link to={`/recipes/${r.slug}`} className="cat-card block" data-testid={`home-cocktail-card-${r.slug}`} aria-label={`${r.name} recipe`}>
                                            <div style={{ aspectRatio: "4/5", overflow: "hidden" }}>
                                                <img src={r.cover_image_url} alt={`${r.name} cocktail`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            </div>
                                            <div className="p-6">
                                                <h3 className="font-display text-white" style={{ fontSize: 22, fontWeight: 500 }}>{r.name}</h3>
                                                <p className="mt-2 text-sm" style={{ color: "var(--ls-text-secondary)", lineHeight: 1.5 }}>{r.description}</p>
                                                <div className="mt-4 flex items-center gap-3 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                                    <Clock size={12} /> {r.prep_minutes} min · {r.difficulty} · {r.glassware}
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))
                        }
                    </div>
                </div>
            </section>

            {/* EXPLORE CLINTON */}
            <section className="section-y" aria-label="Explore Clinton, Missouri" data-testid="home-explore-section">
                <div className="ls-container">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                        <div>
                            <span className="eyebrow">Explore Clinton, Missouri</span>
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                                Make a day of it <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>in Henry County.</em>
                            </h2>
                        </div>
                        <Link to="/local/clinton-mo" className="btn-ghost" data-testid="home-local-all">Full local guide <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-explore-grid">
                        {attractions === null
                            ? Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="cat-card" aria-hidden="true" data-testid={`explore-skel-${i}`}>
                                    <div style={{ aspectRatio: "16/10", background: "linear-gradient(110deg, #161616 30%, #1f1f1f 50%, #161616 70%)", backgroundSize: "200% 100%", animation: "skel 1.4s ease-in-out infinite" }} />
                                </div>
                            ))
                            : attractions.length === 0
                                ? <p style={{ color: "var(--ls-text-muted)" }} data-testid="home-explore-empty">Local guide coming soon.</p>
                                : attractions.map((a, i) => (
                                    <motion.div key={a.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                                        <Link to={`/local/${a.slug}`} className="cat-card block" data-testid={`home-explore-card-${a.slug}`} aria-label={a.name}>
                                            <div style={{ aspectRatio: "16/10", overflow: "hidden" }}>
                                                <img src={a.cover_image_url} alt={a.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            </div>
                                            <div className="p-6">
                                                <div className="flex items-center gap-2 text-xs uppercase tracking-wider" style={{ color: "var(--ls-gold)" }}>
                                                    <MapPin size={11} /> {a.distance_from_store}
                                                </div>
                                                <h3 className="font-display text-white mt-3" style={{ fontSize: 21, fontWeight: 500, lineHeight: 1.2 }}>{a.name}</h3>
                                                <p className="mt-2 text-sm" style={{ color: "var(--ls-text-secondary)", lineHeight: 1.5 }}>{a.short_description}</p>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))
                        }
                    </div>
                </div>
            </section>

            {/* REVIEWS */}
            <section className="section-y" aria-label="Customer Reviews" data-testid="reviews-section">
                <div className="ls-container">
                    <div className="text-center mb-14">
                        <span className="eyebrow">What People Say</span>
                        <h2
                            className="font-display mt-4 text-white"
                            style={{
                                fontSize: "clamp(36px, 5vw, 56px)",
                                fontWeight: 500,
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Locals who{" "}
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                keep coming back.
                            </em>
                        </h2>
                        {reviewSummary && reviewSummary.count > 0 && (
                            <div
                                className="mt-6 inline-flex items-center gap-3"
                                style={{
                                    background: "rgba(245,176,39,0.08)",
                                    border: "1px solid rgba(245,176,39,0.35)",
                                    borderRadius: 999,
                                    padding: "8px 18px",
                                }}
                                data-testid="home-review-pill"
                            >
                                <div className="flex gap-0.5" aria-hidden="true">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star
                                            key={n}
                                            size={12}
                                            fill={n <= Math.round(reviewSummary.average) ? "var(--ls-gold)" : "transparent"}
                                            stroke="var(--ls-gold)"
                                        />
                                    ))}
                                </div>
                                <span style={{ color: "var(--ls-gold)", fontSize: 13, fontWeight: 600 }}>
                                    {reviewSummary.average} from {reviewSummary.count} verified reviews
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {displayReviews.map((r, i) => (
                            <motion.figure
                                key={(r.id || r.author_name) + i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                style={{
                                    background: "var(--ls-panel)",
                                    border: "1px solid var(--ls-border)",
                                    borderRadius: 6,
                                    padding: 32,
                                }}
                                data-testid={`review-card-${i}`}
                            >
                                <div className="flex gap-1 mb-4" aria-label={`${r.rating} out of 5 stars`}>
                                    {[...Array(r.rating)].map((_, idx) => (
                                        <Star key={idx} size={14} fill="var(--ls-gold)" stroke="var(--ls-gold)" />
                                    ))}
                                </div>
                                <blockquote
                                    className="font-display"
                                    style={{
                                        color: "var(--ls-text)",
                                        fontSize: 19,
                                        lineHeight: 1.5,
                                        fontWeight: 400,
                                    }}
                                >
                                    "{r.body}"
                                </blockquote>
                                <figcaption
                                    className="mt-6 text-sm"
                                    style={{ color: "var(--ls-text-muted)" }}
                                >
                                    <strong className="text-white font-medium">{r.author_name}</strong>
                                    {r.author_location ? ` · ${r.author_location}` : ""}
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>

                    <div className="text-center mt-10">
                        <Link
                            to="/reviews"
                            className="btn-outline-gold"
                            style={{ fontSize: 13, padding: "12px 24px" }}
                            data-testid="home-reviews-cta"
                        >
                            Read All Reviews <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* VISIT CTA STRIP */}
            <section
                aria-label="Visit Our Store"
                data-testid="visit-strip"
                style={{
                    background: "var(--ls-panel)",
                    borderTop: "1px solid var(--ls-border)",
                    borderBottom: "1px solid var(--ls-border)",
                }}
            >
                <div className="ls-container py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                        <div>
                            <span className="eyebrow">Visit Our Store</span>
                            <h2
                                className="font-display mt-4 text-white"
                                style={{
                                    fontSize: "clamp(34px, 4.5vw, 52px)",
                                    fontWeight: 500,
                                    lineHeight: 1.05,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                309 S 2nd St, Clinton
                            </h2>
                            <p
                                className="mt-4"
                                style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.6 }}
                            >
                                Just off MO-7 & MO-13. Two minutes from downtown Clinton Square.
                            </p>

                            <ul className="mt-8 space-y-4 text-sm">
                                <li className="flex items-start gap-3">
                                    <MapPin size={18} className="mt-0.5" style={{ color: "var(--ls-gold)" }} />
                                    <span className="text-white">{BIZ.address.full}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Phone size={18} className="mt-0.5" style={{ color: "var(--ls-gold)" }} />
                                    <a href={`tel:${BIZ.phoneTel}`} className="text-white link-underline" data-testid="visit-strip-phone">
                                        {BIZ.phone}
                                    </a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Clock size={18} className="mt-0.5" style={{ color: "var(--ls-gold)" }} />
                                    <div className="text-white">
                                        Mon–Thu 10a–10p · Fri–Sat 10a–11p · Sun 10a–10p
                                    </div>
                                </li>
                            </ul>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BIZ.address.full)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-primary"
                                    data-testid="visit-strip-directions"
                                >
                                    Get Directions <ArrowRight size={16} />
                                </a>
                                <Link to="/visit" className="btn-outline-gold" data-testid="visit-strip-more">
                                    More Info
                                </Link>
                            </div>
                        </div>

                        <div
                            style={{
                                borderRadius: 6,
                                overflow: "hidden",
                                border: "1px solid var(--ls-border-strong)",
                                aspectRatio: "4/3",
                            }}
                        >
                            <iframe
                                title="Liquor Studio location map"
                                src={`https://www.google.com/maps?q=${encodeURIComponent(BIZ.address.full)}&output=embed`}
                                width="100%"
                                height="100%"
                                style={{ border: 0, display: "block", filter: "grayscale(0.4) contrast(1.05)" }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="home-map-iframe"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* REAL STORE STRIP — proof of place */}
            <section className="section-y" aria-label="Real store photos" data-testid="home-real-store" style={{ background: "#070707", borderTop: "1px solid var(--ls-border)", borderBottom: "1px solid var(--ls-border)" }}>
                <div className="ls-container">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                        <div>
                            <span className="eyebrow">Real store. Real Clinton.</span>
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(32px, 4.2vw, 52px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                                Not a stock photo <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>in sight.</em>
                            </h2>
                            <p className="mt-3" style={{ color: "var(--ls-text-secondary)", fontSize: 16, maxWidth: 620 }}>
                                309 S 2nd Street, Clinton, MO. Stop in or take a virtual stroll.
                            </p>
                        </div>
                        <Link to="/visit" className="btn-ghost" data-testid="real-store-visit">Plan your visit <ArrowRight size={14} /></Link>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                        {[
                            { slug: "storefront-night", aspect: "4/3" },
                            { slug: "entrance-stone-wall", aspect: "4/3" },
                            { slug: "wine-aisle", aspect: "4/3" },
                            { slug: "allocated-weller-bottle", aspect: "4/3" },
                        ].map((p) => (
                            <figure key={p.slug} style={{ margin: 0, position: "relative", overflow: "hidden", borderRadius: 6, border: "1px solid var(--ls-border)" }}>
                                <StorePhoto slug={p.slug} loading="lazy" style={{ aspectRatio: p.aspect }} testid={`real-store-photo-${p.slug}`} />
                                {/* Caption pill — always-dark, readable in any theme */}
                                <figcaption style={{ position: "absolute", left: 10, bottom: 10, right: 10 }}>
                                    <span style={{ display: "inline-block", background: "rgba(10,10,10,0.85)", color: "#fff", padding: "8px 14px", borderRadius: 6, fontSize: 11, letterSpacing: 0.5, lineHeight: 1.4 }}>
                                        {STORE_PHOTOS[p.slug].credit}
                                    </span>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* ALLOCATED & RARE — Van Winkle family flex */}
            <section className="section-y" aria-label="Allocated & Rare Bourbon" data-testid="home-allocated">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <span className="eyebrow">Allocated &amp; Rare Bourbon</span>
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(34px, 4.5vw, 56px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                                Yes, we get <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>the allocated ones.</em>
                            </h2>
                            <p className="mt-5" style={{ color: "var(--ls-text-secondary)", fontSize: 17, lineHeight: 1.75 }}>
                                Weller, Eagle Rare, Blanton's, E.H. Taylor, Stagg, and — when our allocation lands — Van Winkle. Our list is a notebook on the back counter, not a marketing line. If you're chasing a bottle, ask. We'll tell you straight what's in, what's coming, and what's gone.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3" data-testid="home-allocated-actions">
                                <Link to="/spirits/category/bourbon" className="btn-primary" data-testid="home-allocated-browse">Browse Bourbon <ArrowRight size={14} /></Link>
                                <a
                                    href={`mailto:${BIZ.email}?subject=${encodeURIComponent("Allocated Bourbon Inquiry")}&body=${encodeURIComponent("Hi Liquor Studio,\n\nI'm interested in your current allocated bourbon list. Specifically:\n- Bottle(s) of interest:\n- Budget range:\n- Pickup timing:\n\nName:\nPhone:\n\nThanks!")}`}
                                    className="btn-outline-gold"
                                    data-testid="home-allocated-inquire"
                                >
                                    Ask About the List <Mail size={14} />
                                </a>
                            </div>
                            <p className="mt-4 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                21+ only. Limit one per customer on most allocated SKUs. No reservations on Pappy releases.
                            </p>
                        </div>
                        <div style={{ position: "relative" }}>
                            <StorePhoto
                                slug="allocated-weller-bottle"
                                style={{ aspectRatio: "3/4", borderRadius: 6, border: "1px solid var(--ls-border-strong)" }}
                                testid="home-allocated-photo"
                            />
                            <div
                                className="absolute -bottom-5 -right-5 hidden md:block px-5 py-4"
                                style={{ background: "var(--ls-bg)", border: "1px solid var(--ls-gold)", borderRadius: 4, maxWidth: 260 }}
                            >
                                <p className="text-xs uppercase tracking-widest" style={{ color: "var(--ls-gold)" }}>Pictured</p>
                                <p className="text-white mt-1" style={{ fontSize: 13, lineHeight: 1.5 }}>
                                    W.L. Weller Special Reserve — the Van Winkle family's everyday wheated bourbon. Allocated.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRENDING FAQs */}
            {trendingFaqs && trendingFaqs.length > 0 && (
                <section className="section-y" aria-label="Trending FAQs" data-testid="home-trending-faqs" style={{ background: "#070707", borderTop: "1px solid var(--ls-border)", borderBottom: "1px solid var(--ls-border)" }}>
                    <div className="ls-container">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                            <div>
                                <span className="eyebrow">Trending Questions</span>
                                <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                                    Quick answers, <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>before you ask.</em>
                                </h2>
                            </div>
                            <Link to="/faq" className="btn-ghost" data-testid="home-faq-all">
                                All questions <ArrowRight size={14} />
                            </Link>
                        </div>
                        <ul style={{ listStyle: "none", padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }} data-testid="home-trending-faq-list">
                            {trendingFaqs.map((f, i) => (
                                <li key={f.id || i}>
                                    <Link
                                        to="/faq"
                                        data-testid={`home-trending-faq-${i}`}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 14,
                                            padding: "18px 20px",
                                            background: "var(--ls-panel)",
                                            border: "1px solid var(--ls-border)",
                                            borderRadius: 8,
                                            color: "var(--ls-text)",
                                            textDecoration: "none",
                                            transition: "border-color 0.2s ease, transform 0.2s ease",
                                        }}
                                    >
                                        <HelpCircle size={18} style={{ color: "var(--ls-gold)", flexShrink: 0 }} />
                                        <span style={{ flex: 1, fontSize: 14, fontWeight: 500, lineHeight: 1.4 }}>{f.question}</span>
                                        <ArrowRight size={14} style={{ color: "var(--ls-text-muted)", flexShrink: 0 }} />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* NEWSLETTER TEASER */}
            <section
                aria-label="Newsletter signup"
                className="section-y"
                data-testid="newsletter-teaser"
            >
                <div className="ls-container">
                    <div
                        className="grain relative overflow-hidden"
                        style={{
                            background: "linear-gradient(135deg, #141414 0%, #1a1a1a 100%)",
                            border: "1px solid var(--ls-border-strong)",
                            borderRadius: 6,
                            padding: "64px 40px",
                            textAlign: "center",
                        }}
                    >
                        <span className="eyebrow">The Pour</span>
                        <h2
                            className="font-display mt-4 text-white"
                            style={{
                                fontSize: "clamp(32px, 4.5vw, 52px)",
                                fontWeight: 500,
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                                maxWidth: 700,
                                margin: "0 auto",
                            }}
                        >
                            New arrivals, allocated drops & local events —{" "}
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                in your inbox.
                            </em>
                        </h2>
                        <form
                            onSubmit={(e) => e.preventDefault()}
                            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
                            aria-label="Newsletter signup"
                        >
                            <label htmlFor="home-newsletter" className="sr-only">
                                Email address
                            </label>
                            <input
                                id="home-newsletter"
                                type="email"
                                placeholder="your@email.com"
                                className="flex-1 text-white"
                                style={{
                                    background: "var(--ls-bg)",
                                    border: "1px solid var(--ls-border-strong)",
                                    borderRadius: 999,
                                    padding: "14px 22px",
                                    fontSize: 15,
                                }}
                                data-testid="home-newsletter-input"
                            />
                            <button type="submit" className="btn-primary" data-testid="home-newsletter-submit">
                                Subscribe
                            </button>
                        </form>
                        <p className="mt-4 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                            No spam. Unsubscribe anytime. 21+ only.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
